import { useCallback, useEffect, useRef, useState } from "react";
import {
  FAMILY_PURCHASE_PATH,
  type FamilyPortalSession,
} from "@shared/types/familyPortal";
import { ApiError, familyApi } from "./api";
import {
  PurchaseReturnScreen,
  PurchaseScreen,
  useOffer,
  VideoCallingCard,
} from "./purchase";
import { useNavigation, type Route } from "./routes";
import { applyTheme } from "./theme";

/**
 * The relatives portal (PORTAL-1 shell, PORTAL-2 login, PORTAL-3 purchase,
 * PORTAL-5 design + copy).
 *
 * Six screens and one piece of state: is there a live session or not.
 *
 *  /              signed out -> the sign-in form; signed in -> the dashboard
 *  /login         the sign-in form, reachable directly
 *  /login/<token> the magic link landing: consume, then replace the URL
 *  /buy           the purchase screen (also the provider's cancel URL)
 *  /buy/return    back from the payment provider
 *
 * Everything the buy flow says lives in `purchase.tsx`; this file stays the shell
 * and the auth screens.
 *
 * WHAT THIS SCREEN MAY SAY ABOUT A RESIDENT: their first name, and whether video
 * calling is active. Nothing else crosses (see FamilyPortalSession) - so there is
 * deliberately no room number, no surname, and no "recent calls" list here, however
 * natural those would feel on a family dashboard.
 *
 * LANGUAGE: Swedish, throughout (PORTAL-5). The buy flow shipped in English
 * behind the provider seam while the auth screens were already Swedish, so a
 * relative who asked for a login link in Swedish, on a page declaring lang="sv",
 * was met with an English "Hello" and asked in English to spend money. The
 * audience is elderly Swedish relatives; the page says so in its own `lang`.
 */
export default function App() {
  const { route, go, replace } = useNavigation();
  const [session, setSession] = useState<FamilyPortalSession | null>(null);
  /** Undefined until the first `/session` probe answers - avoids a sign-in form
   *  flashing in front of an already-signed-in relative on every page load. */
  const [ready, setReady] = useState(false);

  const probe = useCallback(async () => {
    try {
      setSession(await familyApi.session());
    } catch {
      // Any failure here (401, offline) means "show the sign-in form". There is
      // nothing useful to say about which: the remedy is the same.
      setSession(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // A magic-link landing does its own consume; probing first would waste a
    // round-trip and, worse, briefly render "sign in" over a working link.
    if (route.name === "login-token") {
      setReady(true);
      return;
    }
    void probe();
  }, [probe, route.name]);

  const onSignedIn = useCallback(
    (next: FamilyPortalSession) => {
      setSession(next);
      // Replace, never push: the spent token must not stay in the address bar or
      // the back stack.
      replace("/");
    },
    [replace]
  );

  const onSignOut = useCallback(async () => {
    await familyApi.logout().catch(() => undefined);
    setSession(null);
    go("/");
  }, [go]);

  // The portal wears the premise's own TV template (PORTAL-5). It arrives with
  // the session, so a signed-out browser falls back to the stylesheet's ground -
  // which is right: a relative signing out of Solhem should not be left looking
  // at Solhem's colours while they type another address.
  useEffect(() => {
    applyTheme(session?.theme);
  }, [session?.theme]);

  return (
    <div className="app">
      <header className="brand">
        {/* The real mark, not the placeholder square that stood here since
            PORTAL-1. Decorative: the wordmark beside it already names us, so a
            screen reader hearing "Foyer Family" twice gains nothing. */}
        <img
          className="brand__mark"
          src="/icon.svg"
          alt=""
          aria-hidden="true"
        />
        <span className="brand__name">Foyer Family</span>
      </header>
      <main className="stack">
        <Screen
          route={route}
          ready={ready}
          session={session}
          onSignedIn={onSignedIn}
          onSignOut={onSignOut}
          go={go}
          onRefresh={probe}
        />
      </main>
    </div>
  );
}

function Screen(props: {
  route: Route;
  ready: boolean;
  session: FamilyPortalSession | null;
  onSignedIn(s: FamilyPortalSession): void;
  onSignOut(): void;
  go(path: string): void;
  onRefresh(): Promise<void>;
}) {
  const { route, ready, session, onSignedIn, onSignOut, go, onRefresh } = props;

  if (route.name === "login-token") {
    return <MagicLink token={route.token} onSignedIn={onSignedIn} />;
  }
  if (route.name === "not-found") return <NotFound />;
  if (!ready) return <Loading />;
  // The buy screens sit BEHIND the session gate, so a relative who bookmarked
  // /buy - or came back from the payment provider on a device whose session has
  // since expired - gets the sign-in form and lands back here, rather than a
  // purchase screen that cannot say who it is buying for.
  if (!session) return <SignIn />;
  if (route.name === "buy" || route.name === "buy-return") {
    return (
      <SignedInPurchase
        route={route}
        session={session}
        go={go}
        onRefresh={onRefresh}
      />
    );
  }
  return <Dashboard session={session} onSignOut={onSignOut} go={go} />;
}

/**
 * Both buy screens share one offer fetch, mounted at the route so it is re-read
 * on every entry: a purchase decision must never be made against an offer cached
 * from an earlier visit.
 */
function SignedInPurchase(props: {
  route: Route;
  session: FamilyPortalSession;
  go(path: string): void;
  onRefresh(): Promise<void>;
}) {
  const { route, session, go, onRefresh } = props;
  const offer = useOffer();
  const home = useCallback(() => go("/"), [go]);

  if (route.name === "buy-return") {
    return (
      <PurchaseReturnScreen
        session={session}
        offer={offer}
        onActivated={() => void onRefresh()}
        onDone={home}
      />
    );
  }
  return <PurchaseScreen session={session} offer={offer} onDone={home} />;
}

function Loading() {
  return (
    <section className="card" aria-busy="true">
      <p className="card__body">Läser in…</p>
    </section>
  );
}

/**
 * The sign-in form.
 *
 * The success copy is deliberately vague ("if that address is registered"). The
 * server refuses to say whether the address matched - that would be an oracle for
 * "is this person's relative in your building?" - so the UI must not imply it did.
 * Promising "check your inbox" outright would make the honest server dishonest
 * through the front end.
 */
function SignIn() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "throttled">(
    "idle"
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      await familyApi.requestLogin(email);
      setState("sent");
    } catch (err) {
      // 429 is our own send budget, and it is the ONE thing worth saying plainly:
      // a relative who has asked four times needs to know to stop and wait, not to
      // keep pressing a button that looks like it worked.
      setState(
        err instanceof ApiError && err.status === 429 ? "throttled" : "sent"
      );
    }
  }

  if (state === "sent") {
    return (
      <section className="card card--feature">
        <p className="eyebrow">Kolla din e-post</p>
        <h1 className="card__title">Länken är på väg</h1>
        <p className="card__body">
          Om den adressen är registrerad som anhörigkontakt har vi skickat en
          inloggningslänk dit. Länken fungerar en gång och slutar gälla efter 15
          minuter.
        </p>
        <p className="card__note">
          Inget kommit fram? Be boendet skicka en inbjudningslänk direkt till
          dig.
        </p>
      </section>
    );
  }

  return (
    <section className="card card--feature">
      <p className="eyebrow">Foyer Family</p>
      <h1 className="card__title">Logga in</h1>
      <p className="card__body">
        Ange e-postadressen som boendet har registrerat för dig. Vi skickar en
        inloggningslänk — inget lösenord att komma ihåg.
      </p>
      <form className="form" onSubmit={submit}>
        <label className="form__label" htmlFor="email">
          E-postadress
        </label>
        <input
          className="form__input"
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="namn@exempel.se"
        />
        <button
          className="btn btn--primary"
          type="submit"
          disabled={state === "sending"}
        >
          {state === "sending" ? "Skickar…" : "Skicka mig en länk"}
        </button>
        {state === "throttled" ? (
          <p className="form__error" role="alert">
            För många förfrågningar för den adressen. Vänta en stund innan du
            försöker igen.
          </p>
        ) : null}
      </form>
    </section>
  );
}

/**
 * The magic-link landing. Consumes the token EXACTLY once.
 *
 * The `useRef` latch is load-bearing, not defensive tidiness. The token is
 * single-use, and React's StrictMode deliberately mounts effects twice in
 * development: without the latch the second POST spends a link the first one
 * already redeemed, and the relative is shown "this link no longer works" for a
 * link that worked perfectly. A ref survives the double invocation (same component
 * instance) where a local variable does not.
 */
function MagicLink(props: {
  token: string;
  onSignedIn(s: FamilyPortalSession): void;
}) {
  const [failed, setFailed] = useState(false);
  const spentFor = useRef<string | null>(null);

  useEffect(() => {
    if (spentFor.current === props.token) return;
    spentFor.current = props.token;
    let cancelled = false;
    void (async () => {
      try {
        const session = await familyApi.consumeLogin(props.token);
        if (!cancelled) props.onSignedIn(session);
      } catch {
        // Expired, already used, revoked, never existed - the server refuses to
        // distinguish these, and so does this screen. One remedy: get a new link.
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `onSignedIn` is intentionally not a dependency: re-running this effect means
    // re-spending a single-use token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.token]);

  if (failed) {
    return (
      <section className="card card--feature">
        <p className="eyebrow">Inloggning</p>
        <h1 className="card__title">Länken fungerar inte längre</h1>
        <p className="card__body">
          En inloggningslänk går bara att använda en gång, och den slutar gälla
          efter en stund. Beställ en ny så kommer den om ett ögonblick.
        </p>
        <p>
          <a className="link" href="/login">
            Beställ en ny länk
          </a>
        </p>
      </section>
    );
  }

  return (
    <section className="card" aria-busy="true">
      <div className="pulse" aria-hidden="true" />
      <h1 className="card__title">Loggar in dig…</h1>
      <p className="card__body">Ett ögonblick.</p>
    </section>
  );
}

/**
 * The signed-in screen.
 *
 * Its one substantive card is the video-calling status, and that card renders the
 * server's single purchase verdict (PORTAL-3) rather than re-deriving one from
 * `callingEnabled` / `residentPresent` / `videoCallActive`. Two places computing
 * "can they buy?" is two places to disagree - and the way they disagree is by
 * offering to sell a seat the care home assigned five minutes ago.
 *
 * ── Three things, three weights (PORTAL-5) ──────────────────────────────────
 *
 * The greeting, the calling card and sign-out used to be three identical
 * panels, so the screen said nothing about which one mattered - the control
 * that ends a session carried exactly as much weight as the one this portal
 * exists to sell. Now: the greeting is type on the ground, the calling card is
 * the only raised, accent-capped panel, and sign-out is a quiet footer row.
 */
function Dashboard(props: {
  session: FamilyPortalSession;
  onSignOut(): void;
  go(path: string): void;
}) {
  const { session, go } = props;
  const offer = useOffer();

  return (
    <>
      <header className="page-head">
        <h1 className="page-head__title">Hej {session.contactName}</h1>
        <p className="page-head__sub">
          Inloggad som {session.relation || "anhörigkontakt"} på{" "}
          {session.premiseName}.
        </p>
      </header>

      <VideoCallingCard
        session={session}
        offer={offer}
        onBuy={() => go(FAMILY_PURCHASE_PATH)}
      />

      <section className="card card--quiet">
        <button
          className="btn btn--link"
          type="button"
          onClick={props.onSignOut}
        >
          Logga ut
        </button>
        <p className="card__note">
          Du loggas ut från den här enheten. Dina andra enheter förblir
          inloggade.
        </p>
      </section>
    </>
  );
}

function NotFound() {
  return (
    <section className="card">
      <h1 className="card__title">Sidan finns inte</h1>
      <p className="card__body">
        <a className="link" href="/">
          Tillbaka till Foyer Family
        </a>
      </p>
    </section>
  );
}
